"use client";

import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useLocale } from "next-intl";
import { DayPicker, type DropdownProps } from "react-day-picker";
import {
  de,
  enUS,
  es,
  fr,
  ja,
  ko,
  ptBR,
  ru,
  zhCN,
  zhTW,
  type DayPickerLocale,
} from "react-day-picker/locale";

import { SimpleSelect } from "@/components/ui/select";
import type { UiLanguage } from "@/lib/site/site-routing";
import { cn } from "@/lib/utils";

const DAY_PICKER_LOCALES: Record<UiLanguage, DayPickerLocale> = {
  zh: zhCN,
  "zh-tw": zhTW,
  en: enUS,
  ja,
  ko,
  de,
  fr,
  es,
  ru,
  pt: ptBR,
};

function CalendarDropdown({ options = [], value, onChange, disabled }: DropdownProps) {
  return (
    <SimpleSelect
      size="sm"
      className="gap-1 px-2 font-medium"
      contentClassName="min-w-[4.5rem]"
      disabled={disabled}
      value={value != null ? String(value) : undefined}
      onValueChange={(next) =>
        onChange?.({
          target: { value: next },
        } as unknown as React.ChangeEvent<HTMLSelectElement>)
      }
      options={options.map((option) => ({
        value: String(option.value),
        label: option.label,
        disabled: option.disabled,
      }))}
    />
  );
}

export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  const locale = DAY_PICKER_LOCALES[useLocale() as UiLanguage] ?? zhCN;

  return (
    <DayPicker
      data-slot="calendar"
      locale={locale}
      showOutsideDays={showOutsideDays}
      className={cn("relative p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row gap-4",
        month: "flex flex-col gap-3",
        month_caption: "relative flex h-8 items-center justify-center",
        caption_label: "text-sm font-medium",
        nav: "pointer-events-none absolute inset-x-3 top-3 z-10 flex h-8 items-center justify-between",
        button_previous:
          "pointer-events-auto inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40",
        button_next:
          "pointer-events-auto inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40",
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex",
        weekday: "text-muted-foreground w-8 font-normal text-[0.8rem]",
        week: "flex w-full mt-1.5",
        day: "relative size-8 p-0 text-center text-sm focus-within:relative focus-within:z-20",
        day_button:
          "inline-flex size-8 items-center justify-center rounded-md p-0 font-normal hover:bg-accent hover:text-foreground aria-selected:opacity-100",
        today: "bg-accent/60 text-foreground rounded-md",
        outside: "text-muted-foreground/50",
        disabled: "text-muted-foreground/40 opacity-50",
        hidden: "invisible",
        selected:
          "[&>button]:bg-blue-100 [&>button]:text-blue-700 [&>button]:hover:bg-blue-100 [&>button]:hover:text-blue-700 dark:[&>button]:bg-blue-950/50 dark:[&>button]:text-blue-300 dark:[&>button]:hover:bg-blue-950/50 dark:[&>button]:hover:text-blue-300",
        range_start:
          "rounded-l-md bg-accent [&>button]:bg-blue-100 [&>button]:text-blue-700 dark:[&>button]:bg-blue-950/50 dark:[&>button]:text-blue-300",
        range_end:
          "rounded-r-md bg-accent [&>button]:bg-blue-100 [&>button]:text-blue-700 dark:[&>button]:bg-blue-950/50 dark:[&>button]:text-blue-300",
        range_middle:
          "bg-accent [&>button]:bg-transparent [&>button]:text-foreground rounded-none",
        dropdowns: "flex items-center gap-1.5",
        ...classNames,
      }}
      components={{
        Dropdown: CalendarDropdown,
        Chevron: ({ orientation, className: chevronClass }) => {
          const Icon =
            orientation === "left"
              ? ChevronLeft
              : orientation === "right"
                ? ChevronRight
                : ChevronDown;
          return <Icon className={cn("size-4", chevronClass)} />;
        },
      }}
      {...props}
    />
  );
}
